import React, { useState, useEffect, useCallback } from 'react';
import { func, bool } from 'prop-types';

import { Box, Center, Heading } from '@chakra-ui/react';

import { useGuest } from '@/context/guest';
import { useImaginationRoom } from '@invitato/imagination-room';

import useInvitation from '@/hooks/useInvitation';

import playMusic from '@/components/Common/FloatingMusic/utils/playMusic';
import ScrollToDown from '@/components/Common/ScrollToDown/V2';

import { BG_COVER } from '@/constants/assets';
import { BOY_NAME_SHORT, GIRL_NAME_SHORT, IS_BOY_FIRST } from '@/constants/index';
import { ENABLE_SPACE_BRIDE_GROOM } from '@/constants/feature-flags';
import { TEXT_PROPS } from '@/constants/default-props';

/**
 * Function to render Component CoverV3
 * This component will be dissapear after user click show `Undangan`
 * @return {JSX.Element}
 */
function Cover({ loaded, onSeeDetail }) {
  const [loading, setLoading] = useState(false);

  const { onGetFinalContent, onGetFinalName } = useImaginationRoom();
  const { isLoading } = useGuest();
  const isInvitation = useInvitation();

  const handleLoadingComplete = useCallback(() => {
    onSeeDetail();
    setLoading(false);
  }, [onSeeDetail]);

  const handleClickDetail = () => {
    if (loading) return;

    playMusic();
    setLoading(true);
  };

  useEffect(() => {
    // side-effect to show next page after guest data ready
    // @important we use interval to make sure guest data is ready first before we go to next section
    if (loading) {
      let intervalGuest = setInterval(() => {
        if (!isLoading) {
          clearInterval(intervalGuest);
          handleLoadingComplete();
        }
      }, 500);
    }
  }, [loading, isLoading, isInvitation, handleLoadingComplete]);

  return (
    <Box minHeight="100vh" backgroundColor="bgPrimary" pos="relative">
      <Box
        height="100vh"
        width="100%"
        background={`url(${onGetFinalContent('BG_COVER', BG_COVER)})`}
        bgPos="center"
        bgSize="cover"
        padding="2rem"
        color="white"
      >
        <Heading
          marginTop="72px"
          textColor="secondaryColorText"
          fontWeight="normal"
          fontSize="xs"
          textAlign="center"
          letterSpacing="3px"
          marginBottom="8px"
          fontFamily="subHeading"
        >
          {isInvitation ? 'THE WEDDING OF' : 'WEDDING ANNOUNCEMENT'}
        </Heading>
        <Box
          textAlign="center"
          textColor="secondaryColorText"
          textTransform="uppercase"
          letterSpacing="4px"
        >
          <Heading {...TEXT_PROPS.heading}>
            {IS_BOY_FIRST
              ? onGetFinalName('groomName', BOY_NAME_SHORT)
              : onGetFinalName('brideName', GIRL_NAME_SHORT)}{' '}
            &
          </Heading>
          <Heading {...TEXT_PROPS.heading} marginTop={ENABLE_SPACE_BRIDE_GROOM ? '-8px' : '-18px'}>
            {IS_BOY_FIRST
              ? onGetFinalName('brideName', GIRL_NAME_SHORT)
              : onGetFinalName('groomName', BOY_NAME_SHORT)}
          </Heading>
        </Box>
      </Box>
      <Box pos="absolute" bottom="8vh" left="50%" transform="translateX(-50%)">
        <Center>
          {!loaded ? (
            <ScrollToDown loaded={loaded} loading={loading} onClick={handleClickDetail} />
          ) : null}
        </Center>
      </Box>
    </Box>
  );
}

Cover.propTypes = {
  loaded: bool.isRequired,
  onSeeDetail: func.isRequired,
};

export default Cover;
